import { useEffect, useRef, useState, type FormEvent, type ReactNode, type RefObject } from "react";
import { useEveryone, useLantern, useSession } from "../hooks";
import { navigate } from "../router";
import { ago } from "../agent";
import type { AgentToken, Principal, UUID } from "../sync/types";
import { Avatar, Badge, EmptyState, Field, Form, Sheet } from "./ui";
import { locale, t } from "../i18n";

// What each scope a token may carry lets it do.
const scopeNames = (): Record<string, string> => ({
  "channels:read": t("Read channels"),
  "channels:write": t("Create and change channels"),
  "chat:write": t("Post messages"),
  "models:use": t("Use the model gateway"),
  "tasks:assign": t("Assign tasks"),
  "tasks:execute": t("Work on tasks"),
  "principals:identity": t("See people's sign-in identities"),
});

// Scopes that reach beyond what members see, which only admins give and
// new tokens leave out until ticked.
const trustedScopes = ["principals:identity"];

// How long an agent's token lasts, and a person's own, which is shorter.
export const agentLifetimes = (): [number, string][] => [
  [7, t("7 days")],
  [30, t("30 days")],
  [90, t("90 days")],
  [365, t("1 year")],
];
export const personalLifetimes = (): [number, string][] => [
  [1, t("1 day")],
  [7, t("7 days")],
  [30, t("30 days")],
];

const day = (iso: string) => new Date(iso).toLocaleDateString(locale, { dateStyle: "medium" });

// AgentList lists agents, each with a way into what it read and did and,
// for those one may manage (one's own, or any for an admin), into its
// tokens. Admins see each one's owner.
export function AgentList({ agents, onChanged }: { agents: Principal[]; onChanged: () => void }) {
  const session = useSession();
  const state = useLantern();
  const admin = state.me?.role === "admin";
  const [open, setOpen] = useState<Principal | null>(null);
  return (
    <>
      <ul className="rows owned-agents">
        {agents.map((a) => (
          <li key={a.id}>
            <Avatar id={a.id} name={a.display_name || a.handle} size={32} />
            <span className="row-main">
              <span className="row-title">
                {a.display_name || a.handle} {a.status !== "active" && <Badge tone="warn">{a.status === "suspended" ? t("suspended") : a.status}</Badge>}
              </span>
              <span className="row-meta">
                @{a.handle}
                {admin && a.owner_id && ` · ${t("owned by {name}", { name: session.name(a.owner_id) })}`}
              </span>
            </span>
            {(admin || a.owner_id === state.me?.id) && (
              <button className="small" onClick={() => setOpen(a)} aria-label={t("Manage {name}", { name: a.display_name || a.handle })}>
                {t("Manage")}
              </button>
            )}
            <button className="small" onClick={() => navigate(`/agents/${a.id}/traces`)}>
              {t("Traces")}
            </button>
          </li>
        ))}
      </ul>
      {open && (
        <AgentSheet
          agent={open}
          onClose={() => setOpen(null)}
          onChanged={(a) => {
            setOpen(a);
            onChanged();
          }}
        />
      )}
    </>
  );
}

// NewAgentButton makes an agent: one's own, or, for an admin, someone's,
// then opens it to give it a token.
export function NewAgentButton({ onMade }: { onMade: () => void }) {
  const session = useSession();
  const state = useLantern();
  const admin = state.me?.role === "admin";
  const [making, setMaking] = useState(false);
  const [made, setMade] = useState<Principal | null>(null);
  const [handle, setHandle] = useState("");
  const [name, setName] = useState("");
  const [owner, setOwner] = useState(state.me?.id ?? "");
  const [error, setError] = useState("");
  const people = useEveryone(admin && making).filter((p) => p.kind === "user" && p.status === "active" && p.role !== "guest");
  if (!state.me || state.me.role === "guest") return null;

  async function make(e: FormEvent) {
    e.preventDefault();
    setError("");
    try {
      const { agent } = await session.api.makeAgent(handle.trim(), name.trim(), admin && owner !== state.me?.id ? owner : undefined);
      setMaking(false);
      setHandle("");
      setName("");
      setMade(agent);
      onMade();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <>
      <button className="small primary" onClick={() => setMaking(true)}>
        {t("New agent")}
      </button>
      {making && (
        <Sheet title={t("New agent")} onClose={() => setMaking(false)} className="page">
          <Form onSubmit={make} aria-label={t("New agent")}>
            <p className="muted">{t("An agent your own code runs, through Lantern's API, MCP or the Slack API. Give it a token next.")}</p>
            <Field label={t("Display name")}>
              <input value={name} maxLength={80} onChange={(e) => setName(e.target.value)} placeholder={t("Deploy bot")} required />
            </Field>
            <Field label={t("Handle")} hint={t("Lowercase letters, digits, dots, dashes and underscores; people @mention it by this.")}>
              <input value={handle} maxLength={64} pattern="[a-z0-9][a-z0-9._\-]*" onChange={(e) => setHandle(e.target.value.toLowerCase())} placeholder="deploy-bot" required />
            </Field>
            {admin && (
              <Field label={t("Owner")} hint={t("Who answers for it, and manages its tokens.")}>
                <select value={owner} onChange={(e) => setOwner(e.target.value)}>
                  {people.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.id === state.me?.id ? t("You") : `${p.display_name || p.handle} (@${p.handle})`}
                    </option>
                  ))}
                </select>
              </Field>
            )}
            {error && <p className="error">{error}</p>}
            <div className="form-actions">
              <button type="submit">{t("Make agent")}</button>
            </div>
          </Form>
        </Sheet>
      )}
      {made && <AgentSheet agent={made} fresh onClose={() => setMade(null)} onChanged={(a) => (setMade(a), onMade())} />}
    </>
  );
}

// MadeToken is a token just made, shown this once, with a way to copy it.
export function MadeToken({ token, onDone }: { token: string; onDone: () => void }) {
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");
  async function copy() {
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
    } catch {
      setError(t("Copying needs HTTPS here; select the token and copy it."));
    }
  }
  return (
    <div className="invite-made password-made" role="status">
      <p>{t("The new token. Copy it now: it is not shown again.")}</p>
      <div className="invite-made-row">
        <input value={token} readOnly aria-label={t("New token")} onFocus={(e) => e.currentTarget.select()} />
        <button type="button" onClick={() => void copy()}>
          {copied ? t("Copied") : t("Copy")}
        </button>
        <button type="button" className="ghost" onClick={onDone}>
          {t("Done")}
        </button>
      </div>
      {error && <p className="error">{error}</p>}
    </div>
  );
}

// TokenRows lists API tokens: each one's name, what it may do, when it was
// made, last used and expires, and a way to revoke it. who, when given,
// says whom a token signs in as.
export function TokenRows({ tokens, onRevoke, who }: { tokens: AgentToken[]; onRevoke: (tk: AgentToken) => void; who?: (tk: AgentToken) => ReactNode }) {
  return (
    <ul className="rows agent-tokens" aria-label={t("Access tokens")}>
      {tokens.map((tk) => (
        <li key={tk.id}>
          <span className="row-main">
            <span className="row-title">{tk.name || t("Unnamed token")}</span>
            {who && <span className="row-meta">{who(tk)}</span>}
            <span className="badges">
              {tk.scopes.map((s) => (
                <Badge key={s} tone="accent">
                  {scopeNames()[s] ?? s}
                </Badge>
              ))}
            </span>
            <span className="row-meta">
              {[
                t("made {date}", { date: day(tk.created_at) }),
                tk.last_used_at ? t("used {when}", { when: ago(tk.last_used_at) }) : t("never used"),
                t("expires {date}", { date: day(tk.expires_at) }),
              ].join(" · ")}
            </span>
          </span>
          <button className="small danger" onClick={() => onRevoke(tk)} aria-label={t("Revoke {name}", { name: tk.name || t("this token") })}>
            {t("Revoke")}
          </button>
        </li>
      ))}
    </ul>
  );
}

// confirmRevoke asks before a token is revoked.
export const confirmRevoke = (tk: AgentToken) => window.confirm(t("Revoke {name}? Whatever uses it stops at once.", { name: tk.name || t("this token") }));

// TokenForm makes a token: its name, how long it lasts and what it may do.
// before holds fields that come first, such as whom it is for; actions,
// buttons beside Make token. A failure shows in the form.
export function TokenForm({
  title,
  scopes,
  lifetimes,
  onMake,
  before,
  actions,
  nameRef,
}: {
  title: string;
  scopes: string[];
  lifetimes: [number, string][];
  onMake: (name: string, scopes: string[], days: number) => Promise<unknown>;
  before?: ReactNode;
  actions?: ReactNode;
  nameRef?: RefObject<HTMLInputElement | null>;
}) {
  const [chosen, setChosen] = useState<string[]>(() => scopes.filter((s) => !trustedScopes.includes(s)));
  const [name, setName] = useState("");
  const longest = lifetimes[lifetimes.length - 1][0];
  const [days, setDays] = useState(Math.min(90, longest));
  const [error, setError] = useState("");
  // The scopes arrive with the tokens; until one is unticked, all are.
  useEffect(() => setChosen((c) => (c.length ? c : scopes.filter((s) => !trustedScopes.includes(s)))), [scopes]);
  // A shorter choice of lifetimes keeps the one chosen within it.
  useEffect(() => setDays((d) => Math.min(d, longest)), [longest]);

  async function make(e: FormEvent) {
    e.preventDefault();
    setError("");
    try {
      await onMake(name.trim(), chosen, days);
      setName("");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <Form onSubmit={make} className="token-form" aria-label={title}>
      <h3>{title}</h3>
      <div className="inline-fields">
        {before}
        <Field label={t("Name")} hint={t("Where it is used, to tell it apart.")}>
          <input ref={nameRef} value={name} maxLength={60} onChange={(e) => setName(e.target.value)} placeholder={t("CI runner")} />
        </Field>
        <Field label={t("Expires after")}>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
            {lifetimes.map(([d, label]) => (
              <option key={d} value={d}>
                {label}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <fieldset className="checks">
        <legend>{t("It may")}</legend>
        {scopes.map((s) => (
          <label key={s} className="check-row">
            <input type="checkbox" checked={chosen.includes(s)} onChange={(e) => setChosen(e.target.checked ? [...chosen, s] : chosen.filter((x) => x !== s))} />
            {scopeNames()[s] ?? s}
          </label>
        ))}
      </fieldset>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <div className="form-actions">
        {actions}
        <button type="submit" disabled={chosen.length === 0}>
          {t("Make token")}
        </button>
      </div>
    </Form>
  );
}

// AgentSheet is one agent's tokens: each one's name, what it may do and
// when it was last used, revoking it, and making one, shown once. Its
// owner suspends the agent; an admin makes it active again.
function AgentSheet({ agent, fresh, onClose, onChanged }: { agent: Principal; fresh?: boolean; onClose: () => void; onChanged: (a: Principal) => void }) {
  const session = useSession();
  const state = useLantern();
  const admin = state.me?.role === "admin";
  const [tokens, setTokens] = useState<AgentToken[] | null>(null);
  const [scopes, setScopes] = useState<string[]>([]);
  const [shown, setShown] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [managed, setManaged] = useState("");
  // Where the focus goes when what had it goes away.
  const nameRef = useRef<HTMLInputElement>(null);
  const failed = (err: unknown) => setError(err instanceof Error ? err.message : String(err));

  const load = () =>
    session.api.agentTokens(agent.id).then(
      (r) => {
        setTokens(r.tokens);
        setScopes(r.scopes);
      },
      (err) => (err?.code === "agent_managed" ? setManaged(err.message) : failed(err)),
    );
  useEffect(() => {
    void load();
  }, [agent.id]);

  async function revoke(tk: AgentToken) {
    if (!confirmRevoke(tk)) return;
    setError("");
    try {
      await session.api.revokeAgentToken(agent.id, tk.id);
      setShown(null);
      await load();
      nameRef.current?.focus();
    } catch (err) {
      failed(err);
    }
  }

  async function setStatus(status: "active" | "suspended") {
    setError("");
    try {
      await session.api.setAgentStatus(agent.id, status);
      onChanged({ ...agent, status });
    } catch (err) {
      failed(err);
    }
  }

  async function suspend() {
    if (!window.confirm(t("Suspend {name}? Its tokens stop working at once, and it can no longer act. Only an admin can make it active again.", { name: agent.display_name || agent.handle }))) return;
    await setStatus("suspended");
  }

  const suspended = agent.status === "suspended";
  const close = (
    <button type="button" className="ghost" onClick={onClose}>
      {t("Close")}
    </button>
  );
  return (
    <Sheet title={agent.display_name || agent.handle} size="lg" className="page agent-sheet" onClose={onClose}>
      <div className="agent-head">
        <p className="muted">
          @{agent.handle}
          {agent.owner_id && ` · ${t("owned by {name}", { name: session.name(agent.owner_id) })}`} {suspended && <Badge tone="warn">{t("suspended")}</Badge>}
        </p>
        {!managed &&
          (!suspended ? (
            <button className="small danger" onClick={() => void suspend()}>
              {t("Suspend agent")}
            </button>
          ) : (
            admin && (
              <button className="small" onClick={() => void setStatus("active")}>
                {t("Make active again")}
              </button>
            )
          ))}
      </div>
      {suspended && !admin && !managed && <p className="muted">{t("Its tokens do not work while it is suspended. An admin can make it active again.")}</p>}
      {fresh && !shown && !managed && <p className="notice">{t("The agent is made. Give it a token to sign in with.")}</p>}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {managed ? (
        <p className="muted">{managed}</p>
      ) : (
        <>
          {shown && <MadeToken key={shown} token={shown} onDone={() => (setShown(null), nameRef.current?.focus())} />}
          <h3>{t("Access tokens")}</h3>
          {!tokens ? (
            <p className="muted">{t("Loading…")}</p>
          ) : tokens.length === 0 ? (
            <EmptyState icon="🔑" title={t("No tokens")}>
              {t("Make one below for the agent to sign in with.")}
            </EmptyState>
          ) : (
            <TokenRows tokens={tokens} onRevoke={(tk) => void revoke(tk)} />
          )}
          {tokens && (
            <TokenForm
              title={t("New token")}
              scopes={scopes}
              lifetimes={agentLifetimes()}
              nameRef={nameRef}
              actions={close}
              onMake={async (name, chosen, days) => {
                const r = await session.api.issueAgentToken(agent.id, name, chosen, days);
                setShown(r.token);
                await load();
              }}
            />
          )}
        </>
      )}
      {(managed || !tokens) && <div className="form-actions">{close}</div>}
    </Sheet>
  );
}

// useAgents is the workspace's agents, also those no longer active, and a
// way to load them again after a change: one's own, or every one.
export function useAgents(every: boolean): [Principal[], () => void] {
  const session = useSession();
  const state = useLantern();
  const [n, setN] = useState(0);
  const [all, setAll] = useState<Principal[] | null>(null);
  useEffect(() => {
    let live = true;
    session.api.principals().then(
      (r) => live && setAll(r.principals),
      () => {},
    );
    return () => {
      live = false;
    };
  }, [session, n]);
  const me: UUID | undefined = state.me?.id;
  const agents = (all ?? [...session.directory.values()]).filter((p) => p.kind === "agent" && (every || p.owner_id === me));
  return [agents, () => setN((x) => x + 1)];
}
